/**
 * Reusable image field: upload a file from the computer OR paste an image URL.
 * Emits the resulting servable URL via `onChange`, so it drops into any form
 * that stores an image URL (wine label images today, more later). Uploads go to
 * the storage-backed `/media` endpoint.
 */
import { useRef, useState } from 'react';
import { MAX_IMAGE_BYTES } from '@gwc/shared';
import { mediaApi } from '../api/media';
import { ApiError } from '../lib/api';

export function ImageInput({
  value,
  onChange,
  id,
}: {
  value: string;
  onChange: (url: string) => void;
  id?: string;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preview = value.trim();

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    if (file.size > MAX_IMAGE_BYTES) {
      setError('That image is too large (max 8 MB)');
      return;
    }
    setUploading(true);
    try {
      const { url } = await mediaApi.upload(file);
      onChange(url);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = ''; // allow re-picking the same file
    }
  }

  return (
    <div>
      <div className="flex items-start gap-3">
        {preview ? (
          <img
            src={preview}
            alt="Label preview"
            className="h-24 w-20 shrink-0 rounded border border-ink-800 object-contain"
            onError={(e) => ((e.target as HTMLImageElement).style.visibility = 'hidden')}
          />
        ) : (
          <div className="flex h-24 w-20 shrink-0 items-center justify-center rounded border border-dashed border-ink-800 text-[10px] uppercase tracking-wide text-cream-300/40">
            No image
          </div>
        )}

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
              className="hidden"
              onChange={(e) => void handleFile(e.target.files?.[0])}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-ghost text-sm"
            >
              {uploading ? 'Uploading…' : 'Upload a file'}
            </button>
            {preview && (
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  onChange('');
                }}
                className="text-xs text-cream-300/50 hover:text-wine-300"
              >
                Remove
              </button>
            )}
          </div>
          <input
            id={id}
            className="field"
            placeholder="…or paste an image URL"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      </div>
      {error && <p className="mt-1 text-sm text-wine-300">{error}</p>}
    </div>
  );
}
